import { getStripeSessionHeaders } from '@/utils/stripe';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkStripeCustomerId, useStripePricesQuery } from "@/api/vercel";
import { getStripePublishableKey } from "@/config/stripe";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCustomer } from "@/hooks/useCustomer";
import { useExtraDetails } from "@/hooks/useExtraDetails";
import { useHotTipSettings } from "@/hooks/useHotTipSettings";
import { clearAuthStorage, storeAuthData } from "@/utils/auth";
import { trackMetaPurchase } from "@/utils/facebook-pixel-events";
import {
  PAYMENT_FAILED,
  TERMS_HINT,
  describePaymentError,
  isAlreadyPurchased,
} from "@/utils/payment-errors";
import {
  decodeJwtPayload,
  getStoredProductName,
  persistProductName,
} from "@/utils/product";
import {
  PRICE_IDS,
  downgradeSubscription,
  getCustomerDetails,
  getCustomerSubscriptions,
  upgradeSubscription,
} from "@/utils/stripe";
import {
  UPGRADE_EVENT_NAME,
  buysThroughInAppCheckout,
  consumeUpgradeIntent,
  isPaidSubscriptionStatus,
} from "@/utils/upgrade";
import { PRODUCT_LEXI_PREMIUM, PRODUCT_LEXI_SOCIAL } from "@/utils/vars";
import {
  BadgeCheck,
  ChevronRight,
  CreditCard,
  Info,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Phone,
  Settings,
  X,
} from "lucide-react";
import AppVersion from "@/components/AppVersion";
import ExtraDetailsToggle from "@/components/ExtraDetailsToggle";
import TipsDisplayToggle from "@/components/TipsDisplayToggle";
import UpdateContactModal from "@/components/UpdateContactModal";
import CancelSubscription from "@/components/CancelSubscription";
import BottomNavbar from "@/components/bottom-navbar";
import FlameIcon from "@/components/icons/flame-icon";

const GOOGLE_REVIEW_URL = "https://g.page/r/Ca0tYunNC5M8EAE/review";
const currentRecurringSubscription = (subscriptions = []) =>
  subscriptions.find(
    (sub) => sub.interval !== "one_time" &&
      ["active", "trialing", "past_due"].includes(sub.status),
  );
// A checkout that was never paid (card declined or abandoned) leaves an "incomplete"
// subscription in Stripe. It is not a plan (owner, 3 Nov 2026): the account shows as
// Lexi Social, not "Lexi Pro, renews ...".
const NEVER_PAID_STATUSES = ["incomplete", "incomplete_expired"];
const planSubscription = (subscriptions = []) =>
  currentRecurringSubscription(subscriptions) ||
  subscriptions.find((sub) => !NEVER_PAID_STATUSES.includes(sub?.status));

function GoogleGIcon({ className, ...props }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width="1em"
      height="1em"
      className={className}
      {...props}
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function GoogleReviewButton() {
  const [phase, setPhase] = useState("intro"); // intro -> idle -> hover
  const [hoverNonce, setHoverNonce] = useState(0);

  const iconAnimClass =
    phase === "intro"
      ? "early-mail-envelope--intro"
      : phase === "hover"
        ? "early-mail-envelope--hover"
        : "";

  return (
    <a
      href={GOOGLE_REVIEW_URL}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => {
        setPhase("hover");
        setHoverNonce((n) => n + 1);
      }}
      className="early-mail-pill group w-full inline-flex items-center justify-center cursor-pointer border border-[#fba6e1] px-[12px] py-3 rounded-[12px] shadow-[1px_1px_1px_0px_rgba(0,0,0,0.25)] gap-2 text-[0.8em] font-[800] text-[#F30099] uppercase flex-shrink-0 transform origin-center transition-transform duration-150 ease-out hover:scale-[1.02] active:scale-[0.99] bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F30099]/40"
    >
      <GoogleGIcon
        aria-hidden="true"
        key={`${phase}-${hoverNonce}`}
        onAnimationEnd={() => setPhase("idle")}
        className={`w-[18px] h-[18px] relative scale-[1.15] top-[0.02em] early-mail-envelope ${iconAnimClass}`}
      />
      <span>Leave a Google Review</span>
      <ChevronRight
        className="w-4 h-4 text-[#F30099] transition-transform group-hover:translate-x-[1px]"
        aria-hidden="true"
      />
    </a>
  );
}

const maskValue = (value, visible = 4) => {
  if (!value || typeof value !== "string") return value;
  const trimmed = value.trim();
  if (trimmed.length <= visible) return "*".repeat(Math.max(trimmed.length, 1));
  return `${"*".repeat(trimmed.length - visible)}${trimmed.slice(-visible)}`;
};
const normalizeEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : null;
const formatSubscriptionPrice = (
  amount,
  currency = "AUD",
  interval = "month",
) => {
  if (amount === undefined || amount === null) return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return null;
  // Stripe amounts are in cents (smallest currency unit)
  // If amount is >= 100, assume it's in cents and convert to dollars
  // If amount is < 100, assume it's already in dollars (for APIs that pre-convert)
  const normalized = num >= 100 ? num / 100 : num;
  const formatted = normalized.toLocaleString("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const normalizedInterval = (interval || "").toLowerCase();
  if (!normalizedInterval || normalizedInterval === "one_time") {
    return formatted;
  }
  return `${formatted}/${normalizedInterval}`;
};

const UTM_SOURCE = "stripe";
const UTM_MEDIUM = "payment";
const UTM_CAMPAIGN = "payment_complete";

const addUtmParamsToUrl = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("utm_source", UTM_SOURCE);
  url.searchParams.set("utm_medium", UTM_MEDIUM);
  url.searchParams.set("utm_campaign", UTM_CAMPAIGN);
  window.history.replaceState({}, "", url);
};

const toLocalAustralianMobile = (phone) => {
  if (!phone || typeof phone !== "string") return phone || "";
  const trimmed = phone.replace(/\s+/g, "");
  if (trimmed.startsWith("+61")) {
    return `0${trimmed.slice(3)}`;
  }
  if (trimmed.startsWith("61")) {
    return `0${trimmed.slice(2)}`;
  }
  return trimmed;
};

const normalizePhoneForStripe = (phone) => {
  if (!phone || typeof phone !== "string") return phone || "";
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+61")) return cleaned;
  if (cleaned.startsWith("61")) return `+${cleaned}`;
  if (cleaned.startsWith("0")) return `+61${cleaned.slice(1)}`;
  return cleaned;
};

const initialPublishableKey = getStripePublishableKey();
const initialStripePromise = initialPublishableKey
  ? loadStripe(initialPublishableKey)
  : null;

const getTipsDebugState = () => {
  if (typeof window === "undefined") {
    return {
      userType: "UT-FREE",
      tdm: "TDM-2",
      tipsState: "TIPS-M",
      resultsState: "RESULTS-FALSE",
    };
  }

  const product = (getStoredProductName() || "").toLowerCase();
  const isFree = product === "lexi social";
  const showAllTips = localStorage.getItem("showAllTips") === "true";

  return {
    userType: isFree ? "UT-FREE" : "UT-PAID",
    tdm: showAllTips ? "TDM-4" : "TDM-2",
    tipsState: "TIPS-M",
    resultsState: "RESULTS-FALSE",
  };
};

const emitTipsDebugState = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("lexi:tips-state", {
      detail: getTipsDebugState(),
    }),
  );
};

function InAppCheckoutForm({
  email,
  phoneNumber,
  phoneNumberNormalized,
  onSuccess,
  onError,
  onClose,
  isDisabled,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsHint, setTermsHint] = useState("");
  const termsRef = useRef(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    // The button stays tappable so the buyer learns why it won't go through
    if (!termsAccepted) {
      setTermsHint(TERMS_HINT);
      termsRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        receipt_email: email || undefined,
        payment_method_data: {
          billing_details: {
            email: email || undefined,
            phone: phoneNumberNormalized || phoneNumber || undefined,
          },
        },
      },
      redirect: "if_required",
    });

    if (error) {
      const message = describePaymentError(error);
      setStatusMessage(message);
      onError?.(message);
      setIsSubmitting(false);
      return;
    }

    const status = paymentIntent?.status;
    if (
      status === "succeeded" ||
      status === "processing" ||
      status === "requires_capture"
    ) {
      setStatusMessage("Payment submitted. Finalizing your subscription...");
      onSuccess?.(paymentIntent);
    } else {
      setStatusMessage(PAYMENT_FAILED);
      onError?.(PAYMENT_FAILED);
    }

    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        id="in-app-payment-element"
        options={{ layout: { type: "tabs" } }}
      />
      {statusMessage && (
        <div className="text-sm text-gray-700">{statusMessage}</div>
      )}

      {/* Terms and Conditions */}
      <div className="flex items-start gap-3">
        <input
          ref={termsRef}
          type="checkbox"
          id="dashboard-terms-checkbox"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setTermsHint("");
          }}
          aria-describedby={termsHint ? "dashboard-terms-hint" : undefined}
          className="mt-1 w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
        />
        <label
          htmlFor="dashboard-terms-checkbox"
          className="text-sm text-gray-700"
        >
          I agree to the{" "}
          <a
            href="/subscription-agreement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#F30099] hover:text-pink-700 underline"
          >
            Terms and Conditions
          </a>
        </label>
      </div>
      {termsHint && (
        <p
          id="dashboard-terms-hint"
          role="alert"
          className="-mt-2 text-sm text-red-600"
        >
          {termsHint}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!stripe || !elements || isSubmitting || isDisabled}
          className="flex-1 px-4 py-2 bg-[#F30099] text-white rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? "Processing..." : "Confirm and Upgrade"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const getBillingPortalUrl = () => {
  const workspace = import.meta.env.VITE_APP_WORKSPACE;
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  // Default to dev URL for localhost
  if (isLocalhost && !workspace) {
    return "https://billing.stripe.com/p/login/test_eVag0N8FWbYM35KeUU";
  }

  switch (workspace) {
    case "dev":
      return "https://billing.stripe.com/p/login/test_eVag0N8FWbYM35KeUU";
    case "staging":
      return "https://billing.stripe.com/p/login/test_28EaEZ8Zk53F4fy8HmdUY00";
    case "prod":
    default:
      return "https://billing.stripe.com/p/login/7sIfZZ4i0eUW2647ss";
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const {
    customerId: firebaseCustomerId,
    stripeCustomerId: existingStripeCustomerId,
  } = useCustomer();
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [customerId, setCustomerId] = useState(null);
  const [updateModal, setUpdateModal] = useState({ isOpen: false, type: null });
  const [isUpdating, setIsUpdating] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [planError, setPlanError] = useState("");
  const [planErrorCode, setPlanErrorCode] = useState("");
  const [isUpgrading, setIsUpgrading] = useState(false);
  const {
    hotTipMinPrice,
    updateHotTipMinPrice,
    isLoading: isHotTipSettingsLoading,
  } = useHotTipSettings();
  const { showExtraDetails, toggleExtraDetails } = useExtraDetails();
  const [inputValue, setInputValue] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [productName, setProductName] = useState(null);
  const [isNonStripeUser, setIsNonStripeUser] = useState(false);
  const [stripePromise, setStripePromise] = useState(initialStripePromise);
  const [publishableKey, setPublishableKey] = useState(initialPublishableKey);
  const [isInAppCheckoutOpen, setIsInAppCheckoutOpen] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [isCheckoutInitializing, setIsCheckoutInitializing] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [checkoutPhone, setCheckoutPhone] = useState("");
  const [checkoutPriceSummary, setCheckoutPriceSummary] = useState(null);
  const [checkoutSubscriptionId, setCheckoutSubscriptionId] = useState(null);
  const [checkoutPaymentIntentId, setCheckoutPaymentIntentId] = useState(null);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [pendingCustomerId, setPendingCustomerId] = useState(null);
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);

  const { data: stripePricesData, isLoading: isPriceLoading } =
    useStripePricesQuery({
      refetchOnMount: true,
    });

  const hasAutoInitializedPrice = useRef(false);
  const escListenerAttached = useRef(false);

  const upgradeOptions = useMemo(() => {
    if (!stripePricesData?.products) return [];

    return stripePricesData.products
      .filter((product) => product?.prices?.length)
      .filter((product) => product?.name !== PRODUCT_LEXI_SOCIAL)
      .flatMap((product) =>
        (product.prices || []).map((price) => {
          const priceInterval =
            price.recurring?.interval ||
            (price.type === "one_time" ? "one_time" : null);
          return {
            productId: product.id,
            productName: product.name,
            description: product.description,
            priceId: price.id,
            interval: priceInterval || "month",
            priceType:
              price.type ||
              (priceInterval === "one_time" ? "one_time" : "recurring"),
            amount: price.unitAmount ?? price.unit_amount ?? 0,
            currency: price.currency || "aud",
            isPopular: product.metadata?.is_popular === "true",
          };
        }),
      )
      .sort((a, b) => (a.amount || 0) - (b.amount || 0));
  }, [stripePricesData]);

  const defaultUpgradeOption = useMemo(() => {
    if (!upgradeOptions.length) return null;
    const recurringOptions = upgradeOptions.filter(
      (option) =>
        option.priceType !== "one_time" &&
        option.interval !== "one_time" &&
        (option.interval || "").toLowerCase() !== "one_time",
    );
    const sortedRecurring =
      recurringOptions.length > 0
        ? [...recurringOptions].sort(
            (a, b) => (a.amount || 0) - (b.amount || 0),
          )
        : [];
    const lexiProRecurring = sortedRecurring.find(
      (option) =>
        (option.productName || "").toLowerCase() ===
        PRODUCT_LEXI_PREMIUM.toLowerCase(),
    );
    return lexiProRecurring || sortedRecurring[0] || upgradeOptions[0];
  }, [upgradeOptions]);
  // User is on Lexi Social (free tier) if:
  // 1. Product name explicitly matches "Lexi Social", OR
  // 2. No product name AND user is not managed in Stripe
  // This ensures all paid products (Lexi Pro, 7-Day Pass, etc.) are treated as paid
  const isLexiSocial =
    (productName || "").toLowerCase() === PRODUCT_LEXI_SOCIAL.toLowerCase() ||
    (productName === null && isNonStripeUser);

  const setFreemiumPlan = (productNameOverride) => {
    const resolvedProductName =
      productNameOverride || getStoredProductName() || "Lexi Social";
    const storedProductName =
      persistProductName(resolvedProductName) || resolvedProductName;

    setProductName(storedProductName);
    setIsNonStripeUser(true);
    setSubscriptionDetails({
      subscriptions: [
        {
          productName: storedProductName,
          priceId: PRICE_IDS.FREEMIUM,
          amount: "0",
          interval: "month",
          status: "inactive",
        },
      ],
    });
  };

  useEffect(() => {
    const email = sessionStorage.getItem("userEmail");
    const phone = sessionStorage.getItem("phoneNumber");
    const savedPendingCustomerId = sessionStorage.getItem(
      "pendingStripeCustomerId",
    );

    if (!email) {
      navigate("/single-sso");
      return;
    }

    setUserEmail(email);
    if (phone) {
      setUserPhone(phone);
    }

    // Restore pending customerId if exists
    if (savedPendingCustomerId) {
      setPendingCustomerId(savedPendingCustomerId);
    }

    // Verify current billing before using historical payments or cached free flags.
    const initializeUserData = async () => {
      const productName = getStoredProductName();
      setProductName(productName);
      if (productName) {
        persistProductName(productName);
      }

      await fetchCustomerDetails(email);
    };

    initializeUserData();
  }, [navigate]);

  useEffect(() => {
    if (customerId) {
      fetchSubscriptionDetails();
    }
  }, [customerId]);

  useEffect(() => {
    const sub = subscriptionDetails?.subscriptions?.[0];
    const subName =
      sub?.productName || sub?.planName || sub?.subscriptionProduct;
    const status = sub?.status;

    if (subName && isPaidSubscriptionStatus(status)) {
      setProductName(subName);
      persistProductName(subName);
    }
  }, [subscriptionDetails]);

  useEffect(() => {
    if (!isHotTipSettingsLoading && hotTipMinPrice) {
      setInputValue(hotTipMinPrice);
    }
  }, [isHotTipSettingsLoading, hotTipMinPrice]);

  const fetchCustomerDetails = async (email) => {
    try {
      setPlanError("");
      setPlanErrorCode("");
      setIsLoadingPlan(true);
      const phoneRaw = sessionStorage.getItem("phoneNumber");
      // console.info("[dashboard] fetchCustomerDetails: start", {
      //   email,
      //   phone: isDev ? phoneRaw : maskValue(phoneRaw),
      //   phoneLength: phoneRaw?.length ?? 0,
      //   product: productName,
      //   isNonStripeUser,
      // });
      const customerData = await getCustomerDetails(email);

      if (customerData?.billingStatus === "NO_BILLING_CUSTOMER") {
        // Check if user has one-time payments in JWT before setting freemium
        const hasOneTimePayment = await checkAndLoadOneTimePayments();
        if (!hasOneTimePayment) {
          setFreemiumPlan(PRODUCT_LEXI_SOCIAL);
        }
        return null;
      }
      if (!customerData?.customerId) {
        throw new Error("Unable to confirm your plan. Please try again.");
      }

      // Clear free-user flag since we found a Stripe customer
      sessionStorage.removeItem("lexiFreeUser");
      localStorage.removeItem("lexiFreeUser");

      setCustomerId(customerData.customerId);
      // Clear pending customerId since we have a real one now
      setPendingCustomerId(null);
      sessionStorage.removeItem("pendingStripeCustomerId");

      if (customerData.email) {
        setUserEmail(customerData.email);
      }
      if (customerData.phone) {
        setUserPhone(customerData.phone);
      }
      const resolvedProductName =
        customerData?.subscriptionProduct ||
        customerData?.planName ||
        customerData?.productName ||
        productName;
      const resolvedStatus =
        customerData?.subscriptionStatus ||
        customerData?.status ||
        subscriptionDetails?.status;

      if (resolvedProductName && isPaidSubscriptionStatus(resolvedStatus)) {
        setProductName(resolvedProductName);
        persistProductName(resolvedProductName);
      }

      setIsNonStripeUser(false);
      return customerData;
    } catch (error) {
      setPlanErrorCode(error?.code || "");
      setPlanError(
        error?.message || "Unable to load your subscription. Please try again.",
      );
      // On error, keep the email and phone from session storage (already set)
      return null;
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const checkAndLoadOneTimePayments = async () => {
    try {
      const currentToken =
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("authToken");
      if (!currentToken) return false;

      const payload = decodeJwtPayload(currentToken);
      const oneTimePayments = payload?.oneTimePayments;

      if (oneTimePayments && oneTimePayments.length > 0) {
        console.log(
          `Found ${oneTimePayments.length} one-time payment(s) in JWT`,
        );

        // Get the most recent one-time payment
        const mostRecent = oneTimePayments.sort(
          (a, b) => b.created - a.created,
        )[0];

        console.log("Loading one-time payment details:", {
          productName: mostRecent.productName,
          priceId: mostRecent.priceId,
          amount: mostRecent.amount,
          currency: mostRecent.currency,
          created: new Date(mostRecent.created * 1000).toISOString(),
        });

        // Set subscription details to display the one-time payment
        setSubscriptionDetails({
          subscriptions: [
            {
              productName: mostRecent.productName,
              priceId: mostRecent.priceId,
              amount: mostRecent.amount,
              currency: mostRecent.currency,
              interval: "one_time",
              status: "active",
              paymentIntentId: mostRecent.paymentIntentId,
              created: mostRecent.created,
            },
          ],
        });

        // Set product name
        if (mostRecent.productName) {
          setProductName(mostRecent.productName);
          persistProductName(mostRecent.productName);
          console.log("Product name set to:", mostRecent.productName);
        }

        // Clear any free user flags since this is a paid user
        sessionStorage.removeItem("lexiFreeUser");
        localStorage.removeItem("lexiFreeUser");

        setIsNonStripeUser(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error checking one-time payments from JWT:", error);
      return false;
    }
  };

  const ensureCustomerId = async () => {
    if (customerId) return customerId;
    const data = await fetchCustomerDetails(userEmail);
    if (data?.customerId) {
      setCustomerId(data.customerId);
      return data.customerId;
    }
    console.error("[dashboard] ensureCustomerId: unable to resolve customerId");
    return null;
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setIsLoadingPlan(true);
      setPlanError("");
      const subData = await getCustomerSubscriptions(customerId);
      const subscriptions = subData?.subscriptions || [];
      const current = currentRecurringSubscription(subscriptions);
      if (current) {
        setSubscriptionDetails({
          ...subData,
          subscriptions: [current, ...subscriptions.filter((sub) => sub !== current)],
        });
      } else if (!(await checkAndLoadOneTimePayments())) {
        if (subscriptions.length) setSubscriptionDetails(subData);
        else setFreemiumPlan("Lexi Social");
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      setPlanError(
        error?.message || "Unable to load your subscription. Please try again.",
      );
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const handleLogout = () => {
    // Clear both sessionStorage and localStorage completely
    clearAuthStorage();

    try {
      sessionStorage.removeItem("lexiProduct");
      localStorage.removeItem("lexiProduct");
      sessionStorage.removeItem("lexiFreeUser");
      localStorage.removeItem("lexiFreeUser");
    } catch (error) {
      console.warn("Failed to clear product/free-user flags on logout", error);
    }

    navigate("/");
  };

  const handleUpdateContact = async (newValue) => {
    try {
      if (updateModal.type === "email") {
        setUserEmail(newValue);
        sessionStorage.setItem("userEmail", newValue);
        localStorage.setItem("userEmail", newValue);
      } else {
        setUserPhone(newValue);
        sessionStorage.setItem("phoneNumber", newValue);
        localStorage.setItem("phoneNumber", newValue);
      }

      // Refresh user and subscription details to sync with backend changes
      await fetchCustomerDetails(
        updateModal.type === "email" ? newValue : userEmail,
      );
      if (customerId) {
        await fetchSubscriptionDetails();
      }
    } catch (error) {
      console.error(`Error reflecting ${updateModal.type} update:`, error);
    }
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const tokenSubscriptions = decodeJwtPayload(
        sessionStorage.getItem("authToken") || localStorage.getItem("authToken"),
      )?.subscription;
      if (
        buysThroughInAppCheckout({
          isNonStripeUser,
          subscriptionDetails,
          tokenSubscriptions: Array.isArray(tokenSubscriptions)
            ? tokenSubscriptions
            : [],
        })
      ) {
        openInAppCheckout();
        return;
      }

      const resolvedCustomerId = await ensureCustomerId();
      if (!resolvedCustomerId) {
        console.error(
          "[dashboard] handleUpgrade: missing customerId, aborting",
        );
        return;
      }

      const currentPlan = subscriptionDetails?.subscriptions[0]?.priceId;
      let newPriceId;

      // Determine upgrade path
      if (!currentPlan || currentPlan === PRICE_IDS.FREEMIUM) {
        newPriceId = PRICE_IDS.BASIC_MONTHLY;
      } else if (currentPlan === PRICE_IDS.BASIC_MONTHLY) {
        newPriceId = PRICE_IDS.PREMIUM_MONTHLY;
      }

      if (newPriceId) {
        await upgradeSubscription(resolvedCustomerId, newPriceId);
        fetchSubscriptionDetails();
        addUtmParamsToUrl();
      }
    } catch (error) {
      console.error("Error upgrading subscription:", error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleDowngrade = async () => {
    setIsUpgrading(true);
    try {
      if (isNonStripeUser) {
        console.warn(
          "[dashboard] handleDowngrade: non-stripe user cannot downgrade here",
        );
        return;
      }

      const resolvedCustomerId = await ensureCustomerId();
      if (!resolvedCustomerId) {
        console.error(
          "[dashboard] handleDowngrade: missing customerId, aborting",
        );
        return;
      }

      const currentPlan = subscriptionDetails?.subscriptions[0]?.priceId;
      let newPriceId;

      // Determine downgrade path
      if (currentPlan === PRICE_IDS.PREMIUM_MONTHLY) {
        newPriceId = PRICE_IDS.BASIC_MONTHLY;
      } else if (currentPlan === PRICE_IDS.BASIC_MONTHLY) {
        newPriceId = PRICE_IDS.FREEMIUM;
      }

      if (newPriceId) {
        await downgradeSubscription(resolvedCustomerId, newPriceId);
        fetchSubscriptionDetails();
      }
    } catch (error) {
      console.error("Error downgrading subscription:", error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCurrentPlanDetails = () => {
    if (planError) return { name: planErrorCode === "BILLING_LINK_REQUIRED" ? "Account linking required" : "Subscription unavailable", status: "error" };
    const sub = planSubscription(subscriptionDetails?.subscriptions);

    if (!sub) {
      console.log(
        "[getCurrentPlanDetails] No subscription found, defaulting to Lexi Social",
      );
      return {
        name: "Lexi Social",
        price: "FREE",
        status: "inactive",
      };
    }

    // Handle one-time payments differently
    const isOneTime = sub.interval === "one_time";

    console.log("[getCurrentPlanDetails] Found subscription:", {
      productName: sub.productName,
      interval: sub.interval,
      isOneTime,
      amount: sub.amount,
      currency: sub.currency,
      status: sub.status,
    });

    return {
      name: sub.productName || "Premium Plan",
      price: isNonStripeUser && sub.productName === PRODUCT_LEXI_SOCIAL && Number(sub.amount) === 0
        ? "FREE"
        : isOneTime
        ? formatSubscriptionPrice(sub.amount, sub.currency, "one_time")
        : formatSubscriptionPrice(sub.amount, sub.currency, sub.interval) ||
          `$${sub.amount}/${sub.interval}`,
      status: sub.status,
      renewalDate: isOneTime ? null : sub.currentPeriodEnd,
      isOneTime,
      cancelAtPeriodEnd: isOneTime ? false : sub.cancelAtPeriodEnd,
      cancelAt: isOneTime ? null : sub.cancelAt,
      purchaseDate: isOneTime ? sub.created : null,
    };
  };

  const planDetails = useMemo(() => {
    console.log(
      "[planDetails] Recalculating plan details with subscriptionDetails:",
      subscriptionDetails,
    );
    return getCurrentPlanDetails();
  }, [subscriptionDetails, planError, planErrorCode, isNonStripeUser]);

  const handleHotTipChange = (e) => {
    const val = e.target.value;
    setInputValue(val);

    // Only update if the value is not empty
    if (val !== "") {
      updateHotTipMinPrice(val);
    }
  };

  const handleHotTipBlur = () => {
    // If left empty, set back to 0 (disabled)
    if (inputValue === "") {
      setInputValue("0");
      updateHotTipMinPrice("0");
    }
  };

  const handleHotTipSave = () => {
    // This is just for visual feedback since the value is already auto-saved
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
    }, 2000);
  };

  const startInAppCheckout = async (
    emailValue,
    phoneValue,
    priceIdOverride = null,
  ) => {
    setIsCheckoutInitializing(true);
    setCheckoutError("");
    const resolvedPriceId =
      priceIdOverride ||
      selectedPriceId ||
      defaultUpgradeOption?.priceId ||
      null;
    const selectedOption = upgradeOptions.find(
      (option) => option.priceId === resolvedPriceId,
    );

    if (selectedOption) {
      setCheckoutPriceSummary({
        amount: selectedOption.amount,
        currency: selectedOption.currency,
        interval: selectedOption.interval,
        priceType: selectedOption.priceType,
        productName: selectedOption.productName,
        priceId: selectedOption.priceId,
      });
    } else {
      setCheckoutPriceSummary(null);
    }
    setSelectedPriceId(resolvedPriceId);

    try {
      const normalizedPhone = normalizePhoneForStripe(phoneValue || userPhone);
      const isOneTimePayment =
        selectedOption?.priceType === "one_time" ||
        selectedOption?.interval === "one_time";

      // Prefer existingStripeCustomerId from JWT, then pendingCustomerId, then customerId
      // Now works for both recurring and one-time payments
      const existingCustomerId =
        existingStripeCustomerId || pendingCustomerId || customerId;

      if (existingCustomerId) {
        console.log("Reusing existing Stripe customer:", existingCustomerId);
      } else {
        console.log(
          "Creating new Stripe customer for email:",
          emailValue || userEmail,
        );
      }

      const response = await fetch("/api/stripe/create-in-app-subscription", {
        method: "POST",
        headers: getStripeSessionHeaders(),
        body: JSON.stringify({
          pricingTier: resolvedPriceId ? undefined : "monthly",
          priceId: resolvedPriceId || undefined,
          email: emailValue || userEmail,
          phoneNumber: normalizedPhone || userPhone,
          customerId: existingCustomerId,
          previousSubscriptionId: checkoutSubscriptionId || undefined,
          previousPaymentIntentId: checkoutPaymentIntentId || undefined,
          termsAccepted: true,
          termsAcceptedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();
      // Paid already (an upgrade before the dashboard caught up): refresh the
      // dashboard as after a payment, rather than selling the plan again
      if (isAlreadyPurchased(response, data)) {
        finishPurchase();
        return;
      }
      if (!response.ok) {
        throw new Error(
          data?.message || data?.error || "Unable to start checkout.",
        );
      }

      // Link Stripe customer to Firebase and refresh JWT if a new customer was created
      if (data.customerId && firebaseCustomerId && !existingStripeCustomerId) {
        console.log(
          "Linking new Stripe customer to Firebase:",
          data.customerId,
        );
        try {
          await linkStripeCustomerId(firebaseCustomerId, data.customerId);
          // Refresh JWT to get updated token with stripeCustomerId
          await refreshJWTAfterPayment();
          console.log("Stripe customer linked and JWT refreshed");
        } catch (linkError) {
          console.error("Error linking Stripe customer:", linkError);
          // Continue with checkout even if linking fails
        }
      }

      if (data.publicKey && data.publicKey !== publishableKey) {
        setPublishableKey(data.publicKey);
        setStripePromise(loadStripe(data.publicKey));
      }

      // Store customerId temporarily, don't update main customerId until payment succeeds
      if (data.customerId && !customerId) {
        setPendingCustomerId(data.customerId);
        // Persist to sessionStorage so we can reuse even after page refresh
        sessionStorage.setItem("pendingStripeCustomerId", data.customerId);
      }

      const normalizedInterval =
        data.interval ||
        (data.priceType === "one_time" ? "one_time" : null) ||
        (selectedOption?.priceType === "one_time"
          ? "one_time"
          : selectedOption?.interval) ||
        null;
      const resolvedPriceType =
        data.priceType || selectedOption?.priceType || null;

      setCheckoutClientSecret(data.clientSecret);
      setCheckoutPriceSummary({
        amount: data.amount,
        currency: data.currency,
        interval: normalizedInterval,
        priceType: resolvedPriceType,
        productName: data.productName,
        priceId: data.priceId || resolvedPriceId,
      });
      setCheckoutSubscriptionId(data.subscriptionId || null);
      setCheckoutPaymentIntentId(data.paymentIntentId || null);
    } catch (error) {
      setCheckoutError(
        error.message || "Unable to start checkout. Please try again.",
      );
    } finally {
      setIsCheckoutInitializing(false);
    }
  };

  // Auto-select default option when modal opens, but don't initialize checkout
  useEffect(() => {
    if (!isInAppCheckoutOpen) return;
    if (hasAutoInitializedPrice.current) return;
    if (selectedPriceId) return;
    const optionToUse = defaultUpgradeOption || upgradeOptions[0];
    if (!optionToUse?.priceId) return;

    hasAutoInitializedPrice.current = true;
    setSelectedPriceId(optionToUse.priceId);
    setCheckoutPriceSummary({
      amount: optionToUse.amount,
      currency: optionToUse.currency,
      interval: optionToUse.interval,
      priceType: optionToUse.priceType,
      productName: optionToUse.productName,
      priceId: optionToUse.priceId,
    });
    // Don't call startInAppCheckout here - let user click Continue button
  }, [
    defaultUpgradeOption,
    isInAppCheckoutOpen,
    selectedPriceId,
    upgradeOptions,
  ]);

  // Close checkout modal on ESC key
  useEffect(() => {
    if (!isInAppCheckoutOpen || escListenerAttached.current) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeInAppCheckout();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    escListenerAttached.current = true;

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      escListenerAttached.current = false;
    };
  }, [isInAppCheckoutOpen]);

  const openInAppCheckout = () => {
    const prefillEmail = userEmail;
    const prefillPhone = toLocalAustralianMobile(userPhone);
    setCheckoutEmail(prefillEmail);
    setCheckoutPhone(prefillPhone);
    setIsInAppCheckoutOpen(true);
    // Don't initialize checkout yet - wait for user to select plan and click continue
  };

  const closeInAppCheckout = () => {
    setIsInAppCheckoutOpen(false);
    setCheckoutClientSecret("");
    setCheckoutError("");
    setCheckoutPriceSummary(null);
    setSelectedPriceId(null);
    // Keep pendingCustomerId, checkoutSubscriptionId, and checkoutPaymentIntentId
    // so we can reuse the incomplete customer if user reopens modal
    hasAutoInitializedPrice.current = false;
  };

  const handleSelectUpgradeOption = (option) => {
    if (!option?.priceId) return;
    setSelectedPriceId(option.priceId);
    // Update price summary for display, but don't initialize Stripe checkout yet
    setCheckoutPriceSummary({
      amount: option.amount,
      currency: option.currency,
      interval: option.interval,
      priceType: option.priceType,
      productName: option.productName,
      priceId: option.priceId,
    });
    // Clear Stripe component so user has to click Continue to Payment again
    setCheckoutClientSecret("");
    setCheckoutError("");
  };

  const refreshJWTAfterPayment = async () => {
    try {
      // Get current JWT token from storage
      const currentToken =
        sessionStorage.getItem("authToken") ||
        localStorage.getItem("authToken");

      if (!currentToken) {
        console.warn("Cannot refresh JWT: no token found");
        return;
      }

      console.log("Refreshing JWT token after payment...");

      // Call the refresh JWT endpoint with current token
      const response = await fetch("/api/refresh-jwt-after-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: currentToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn(
          "Failed to refresh JWT:",
          errorData.message || "Unknown error",
        );
        return;
      }

      const data = await response.json();

      // If JWT token is returned, update it
      if (data.token) {
        // Always update the token in both sessionStorage and localStorage
        sessionStorage.setItem("authToken", data.token);
        localStorage.setItem("authToken", data.token);

        // Dispatch custom event to notify other components that token was updated
        window.dispatchEvent(new Event("authTokenUpdated"));

        // Update product name from new token
        const payload = decodeJwtPayload(data.token);
        const subscription = payload?.subscription;
        const oneTimePayments = payload?.oneTimePayments;

        // Check subscriptions first
        if (
          subscription &&
          Array.isArray(subscription) &&
          subscription.length > 0
        ) {
          const activeSubscription =
            subscription.find(
              (sub) => sub.status === "active" || sub.status === "trialing",
            ) || subscription[0];

          const productName =
            activeSubscription?.items?.[0]?.price?.product?.name;
          if (productName) {
            persistProductName(productName);
          } else {
            persistProductName("Lexi Social");
          }
        }
        // Check one-time payments (e.g., 7-Day Pass)
        else if (
          oneTimePayments &&
          Array.isArray(oneTimePayments) &&
          oneTimePayments.length > 0
        ) {
          // Use the most recent one-time payment
          const mostRecent = oneTimePayments.sort(
            (a, b) => b.created - a.created,
          )[0];
          const productName = mostRecent?.productName;
          if (productName) {
            persistProductName(productName);
          } else {
            persistProductName("Lexi Social");
          }
        }
        // No subscriptions or one-time payments
        else {
          persistProductName("Lexi Social");
        }

        // Also update via storeAuthData if verificationSid exists (for session management)
        const verificationSid =
          sessionStorage.getItem("verificationSid") ||
          localStorage.getItem("verificationSid");
        if (verificationSid) {
          const email = userEmail || sessionStorage.getItem("userEmail");
          const phoneNumber =
            userPhone || sessionStorage.getItem("phoneNumber");
          storeAuthData(verificationSid, email, phoneNumber, data.token);
        }

        console.log(
          "JWT token refreshed successfully with new subscription details",
        );
      } else {
        console.warn("No token returned from refresh endpoint");
      }
    } catch (error) {
      console.error("Error refreshing JWT after payment:", error);
    }
  };

  const handleCheckoutSuccess = async (paymentIntent) => {
    // Meta Purchase for this payment, deduplicated on the PaymentIntent ID.
    // Stripe amounts are in cents.
    trackMetaPurchase({
      value: (paymentIntent?.amount ?? checkoutPriceSummary?.amount ?? 0) / 100,
      currency: paymentIntent?.currency || checkoutPriceSummary?.currency,
      productName: checkoutPriceSummary?.productName || PRODUCT_LEXI_PREMIUM,
      eventId: paymentIntent?.id || checkoutPaymentIntentId,
    });

    finishPurchase();
  };

  // Also used when the checkout finds the plan already paid for (an upgrade
  // before the dashboard caught up), which must not report another Purchase
  const finishPurchase = () => {
    // Capture the customerId and check if this was a one-time payment
    const resolvedCustomerId = pendingCustomerId || customerId;
    const isOneTimePayment =
      checkoutPriceSummary?.priceType === "one_time" ||
      checkoutPriceSummary?.interval === "one_time";

    // Commit pending customerId to main state if exists
    if (pendingCustomerId && !customerId) {
      setCustomerId(pendingCustomerId);
    }

    closeInAppCheckout();

    // Clear all checkout state after successful payment
    setPendingCustomerId(null);
    setCheckoutSubscriptionId(null);
    setCheckoutPaymentIntentId(null);
    sessionStorage.removeItem("pendingStripeCustomerId");

    // Show loading modal while upgrading
    setIsUpgradeLoading(true);

    // Add a slight delay to ensure the webhook on the backend stripe finishes the update
    // This matches the pattern used in onboarding.jsx
    setTimeout(async () => {
      // Refresh JWT token with new subscription/payment details
      await refreshJWTAfterPayment();

      // Get updated product name and payment info from refreshed token
      const updatedProductName = getStoredProductName();
      if (updatedProductName) {
        setProductName(updatedProductName);
        persistProductName(updatedProductName);

        // Dispatch product update event to notify other components
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("lexi:product-updated", {
              detail: { productName: updatedProductName },
            }),
          );
        }
      }

      // Extract one-time payment details from JWT for display
      if (isOneTimePayment) {
        // Use checkAndLoadOneTimePayments to handle the display update
        const hasOneTimePayment = await checkAndLoadOneTimePayments();
        if (!hasOneTimePayment) {
          console.warn("Expected one-time payment but none found in JWT");
        }
      }

      // Update isNonStripeUser based on new product
      const isFreeUser =
        (updatedProductName || "").toLowerCase() ===
        PRODUCT_LEXI_SOCIAL.toLowerCase();
      setIsNonStripeUser(isFreeUser);

      // Only fetch customer details for recurring subscriptions, not one-time payments
      if (!isOneTimePayment) {
        const email = userEmail || sessionStorage.getItem("userEmail");
        if (email) {
          const customerData = await fetchCustomerDetails(email);

          // Use the resolved customerId to fetch subscription details
          if (customerData?.customerId || resolvedCustomerId) {
            try {
              const finalCustomerId =
                customerData?.customerId || resolvedCustomerId;
              const subData = await getCustomerSubscriptions(finalCustomerId);
              setSubscriptionDetails(subData);
            } catch (error) {
              console.error(
                "Error fetching subscription details after payment:",
                error,
              );
            }
          }
        }
      } else {
        console.log("One-time payment completed - displaying from JWT");
      }

      addUtmParamsToUrl();

      // Force a re-render by updating a state after a short delay
      // This ensures all components reflect the new subscription status
      setTimeout(() => {
        // Re-check product name from storage to ensure UI is updated
        const finalProductName = getStoredProductName();
        if (finalProductName) {
          setProductName(finalProductName);
        }

        // Dispatch another event to ensure all listeners are notified
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("lexi:product-updated", {
              detail: { productName: finalProductName || updatedProductName },
            }),
          );
        }

        // Hide loading modal after everything is complete
        setIsUpgradeLoading(false);
      }, 1000);
    }, 2000); // Wait 2 seconds for Stripe webhook to process (matching onboarding.jsx)
  };

  const handleCheckoutError = (message) => {
    setCheckoutError(message || "Payment failed. Please try again.");
  };

  // Normalize upgrade requests coming from other parts of the app
  useEffect(() => {
    const maybeHandleUpgrade = () => {
      if (isLoadingPlan) return;
      if (!consumeUpgradeIntent()) return;

      if (isNonStripeUser) {
        openInAppCheckout();
      } else {
        handleUpgrade();
      }
    };

    maybeHandleUpgrade();
    window.addEventListener(UPGRADE_EVENT_NAME, maybeHandleUpgrade);
    return () =>
      window.removeEventListener(UPGRADE_EVENT_NAME, maybeHandleUpgrade);
  }, [handleUpgrade, isLoadingPlan, isNonStripeUser, openInAppCheckout]);

  // Keep debug banner in sync on dashboard (even when tips component isn't mounted)
  useEffect(() => {
    emitTipsDebugState();

    const handleTipsToggle = () => emitTipsDebugState();
    const handleStorage = (event) => {
      if (
        !event.key ||
        event.key === "showAllTips" ||
        event.key === "lexiProduct"
      ) {
        emitTipsDebugState();
      }
    };

    window.addEventListener("tipsDisplayChanged", handleTipsToggle);
    window.addEventListener("lexi:product-updated", handleTipsToggle);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("tipsDisplayChanged", handleTipsToggle);
      window.removeEventListener("lexi:product-updated", handleTipsToggle);
      window.removeEventListener("storage", handleStorage);
    };
  }, [productName]);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-white px-4 py-6 shadow">
          <h1 className="text-2xl font-bold">Dashboard</h1>
        </div>

        {/* Main Content */}
        <div className="p-4 space-y-4">
          {/* Account Details Button */}
          <div className="bg-white rounded-lg shadow">
            <button
              onClick={() => setIsSettingsOpen(!isSettingsOpen)}
              className="w-full px-4 py-4 flex items-center justify-between"
            >
              <div className="flex items-center">
                <Settings className="w-5 h-5 text-gray-500 mr-3" />
                <span className="font-medium">Account Details</span>
              </div>
              <ChevronRight
                className={`w-5 h-5 text-gray-500 transform transition-transform ${isSettingsOpen ? "rotate-90" : ""}`}
              />
            </button>

            {/* Settings Panel */}
            {isSettingsOpen && (
              <div className="px-4 pb-4 space-y-4 border-t">
                {/* Email */}
                <div className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-2" />
                      <span>{userEmail}</span>
                    </div>
                    {!isLexiSocial && (
                      <button
                        onClick={() =>
                          setUpdateModal({ isOpen: true, type: "email" })
                        }
                        className="text-sm text-[#F30099] hover:text-pink-700"
                      >
                        Update
                      </button>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div className="pt-2 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="w-4 h-4 mr-2" />
                      <span>{userPhone}</span>
                    </div>
                    {!isLexiSocial && (
                      <button
                        onClick={() =>
                          setUpdateModal({ isOpen: true, type: "phone" })
                        }
                        className="text-sm text-[#F30099] hover:text-pink-700"
                      >
                        Update
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Top/Top Tip Min Price Setting */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <FlameIcon className="w-5 h-5 text-gray-500 mr-3" />
                <span className="font-medium">Top/Hot Tip Price</span>
              </div>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="1000"
                  step="0.01"
                  className="w-16 border border-gray-300 rounded px-2 py-1 text-center mr-2"
                  value={isHotTipSettingsLoading ? "" : inputValue}
                  onChange={handleHotTipChange}
                  onBlur={handleHotTipBlur}
                  disabled={isHotTipSettingsLoading}
                />
                <button
                  className={`px-2 py-1 rounded text-xs ${saveSuccess ? "bg-green-500 text-white" : "bg-[#F30099] text-white"}`}
                  onClick={handleHotTipSave}
                  disabled={isHotTipSettingsLoading}
                >
                  {saveSuccess ? "Saved!" : "Save"}
                </button>
              </div>
            </div>
            <div className="px-4 pb-4 text-sm text-gray-600">
              Minimum Win odds threshold to show Lexi&apos;s Hot Tip flame. Set
              to 0 to disable.
            </div>
          </div>

          {/* Tips Display Setting */}
          <div className="relative bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5 text-gray-500 mr-3"
                >
                  <path d="M12 6v12m-8-6h16"></path>
                </svg>
                <span className="font-medium">Tip Display Mode</span>
              </div>
              <TipsDisplayToggle forceTopOnly={isLexiSocial} />
            </div>
            <div className="px-4 pb-4 text-sm text-gray-600">
              {isLexiSocial
                ? "Show Lexi's top 2 tips (default), or all 4 tips (best for exotics)"
                : "Show all 4 of Lexi's tips (default, best for exotics), or just the top 2"}
            </div>
            {isLexiSocial && (
              <div className="px-4 pt-4 pb-4 border-y border-gray-100 bg-gradient-to-r from-[#eec4db] to-[#fcdbdb]">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm">
                      <Lock
                        className="w-5 h-5 text-gray-700"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold mb-1 text-gray-900">
                        Unavailable on Lexi Social
                      </p>
                      <p className="text-xs text-gray-600">
                        Upgrade to view all 4 tips
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Extra Details Setting */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-gray-500 mr-3" />
                <span className="font-medium">Extra Details</span>
                <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full">
                  Experimental
                </span>
              </div>
              <ExtraDetailsToggle />
            </div>
            <div className="px-4 pb-4 text-sm text-gray-600">
              Show extended horse, jockey and trainer information
            </div>
          </div>

          {/* Subscription Plan */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 text-gray-500 mr-3" />
                <span className="font-medium">Current Subscription</span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div>
                {isLoadingPlan ? (
                  <div className="animate-pulse">
                    <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded"></div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium">{planDetails.name}</p>
                    {planDetails.price && (
                      <p className="text-sm text-gray-600">
                        {planDetails.price}
                      </p>
                    )}
                    {planDetails.purchaseDate && (
                      <p className="text-xs text-gray-500 mt-1">
                        Purchased on{" "}
                        {formatDate(
                          new Date(
                            planDetails.purchaseDate * 1000,
                          ).toISOString(),
                        )}
                      </p>
                    )}
                    {planDetails.renewalDate && !planDetails.purchaseDate && (
                      <p className="text-xs text-gray-500 mt-1">
                        {planDetails.cancelAtPeriodEnd
                          ? `Cancels on ${formatDate(planDetails.cancelAt)}`
                          : `Renews on ${formatDate(planDetails.renewalDate)}`}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {!isLoadingPlan &&
                  planDetails.status !== "error" &&
                  isLexiSocial && (
                    <div className="space-y-2">
                      {planDetails.status === "active" &&
                        !planDetails.cancelAtPeriodEnd && (
                          <button
                            onClick={handleUpgrade}
                            disabled={isUpgrading}
                            className="px-4 py-2 bg-[#F30099] text-sm text-white rounded-lg hover:bg-pink-700 transition-colors block shrink-0"
                          >
                            Upgrade
                          </button>
                        )}
                      {(!planDetails.status ||
                        planDetails.status === "inactive") &&
                        !isLoadingPlan &&
                        isNonStripeUser && (
                          <button
                            onClick={openInAppCheckout}
                            className="w-full px-3 py-2 bg-[#F30099] font-semibold text-sm text-white rounded-lg hover:bg-pink-700 transition-colors shrink-0"
                          >
                            Upgrade
                          </button>
                        )}
                    </div>
                  )}

              </div>
            </div>
            {planError && (
              <p role="alert" className="mt-3 text-sm text-red-700">{planError}</p>
            )}
            {customerId && subscriptionDetails && (
              <CancelSubscription
                customerId={customerId}
                plan={planDetails}
                onCancelled={fetchSubscriptionDetails}
              />
            )}
          </div>

          {/* Feedback */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <details className="group">
              <summary className="list-none cursor-pointer px-4 py-4 flex items-center justify-between select-none">
                <span className="font-medium inline-flex items-center gap-2">
                  Feedback
                  <GoogleGIcon aria-hidden="true" className="w-4 h-4" />
                </span>
                <ChevronRight className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-4 pb-4 space-y-4 border-t">
                <div className="pt-4">
                  <p className="text-sm font-semibold text-gray-900">
                    Google Review
                  </p>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-4">
                      If Lexi’s tips have helped you enjoy the races 🏇 (or land
                      a nice winner 🏆), we’d love a quick{" "}
                      <strong>Google review</strong>. A few kind words go a long
                      way, and feel free to add a photo 📷 of your winning
                      ticket or celebrations 🥳 too!
                    </p>
                    <GoogleReviewButton />
                    <p className="mt-2 text-[11px] text-gray-500 break-all text-center">
                      Link:{" "}
                      <a
                        href={GOOGLE_REVIEW_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-[#F30099]"
                      >
                        {GOOGLE_REVIEW_URL}
                      </a>
                    </p>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="h-px bg-gray-200 flex-1" />
                    <span className="text-[11px] font-semibold text-gray-500">
                      OR
                    </span>
                    <div className="h-px bg-gray-200 flex-1" />
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <div className="w-full">
                      <img
                        src="/images/lexi-google-review-qr-code.png"
                        alt="Google review QR code for Lexi.tips"
                        className="w-full max-w-[260px] mx-auto sm:mx-0 bg-white"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      <p className="text-sm font-semibold text-gray-900">
                        Scan the QR code
                      </p>
                      <p className="mt-1">
                        Open your phone camera, point it at the code, then tap
                        the link that appears to leave a Google review.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full mt-6 px-4 py-3 flex items-center justify-center bg-white rounded-lg shadow text-deepPink hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 mr-2" />
            <span>Log Out</span>
          </button>
        </div>

        {/* Version number */}
        <AppVersion />
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar />

      {/* In-app Stripe checkout modal */}
      {isInAppCheckoutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeInAppCheckout}
          />
          <div className="relative bg-white rounded-lg shadow-lg w-[95%] max-w-4xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Upgrade</h3>
                <p className="text-sm text-gray-600">
                  Complete checkout without leaving the app. <br />
                  Powered by Stripe Payment Element.
                </p>
              </div>
              <button
                type="button"
                onClick={closeInAppCheckout}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">
                  Choose your plan
                </p>
                {isCheckoutInitializing && (
                  <span className="text-xs text-gray-500">Updating...</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {upgradeOptions.map((option) => (
                  <button
                    key={option.priceId}
                    type="button"
                    onClick={() => handleSelectUpgradeOption(option)}
                    disabled={isCheckoutInitializing}
                    className={`w-full text-center relative border rounded-lg px-5 py-8 transition-all flex flex-col items-center justify-center gap-2 ${
                      selectedPriceId === option.priceId
                        ? "border-[#F30099] ring-2 ring-[#F30099]/40 bg-pink-50"
                        : "border-gray-200 hover:border-[#F30099]"
                    } ${
                      option.productName?.toLowerCase?.().includes("pro")
                        ? "bg-gradient-to-b from-pink-50 to-white border-[#F30099]"
                        : ""
                    }`}
                  >
                    {option.productName?.toLowerCase?.().includes("pro") && (
                      <span className="inline-flex items-center absolute top-2 left-2 gap-1 px-2 py-1 text-[11px] font-semibold text-[#F30099] bg-pink-100 rounded-full">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        Best value
                      </span>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">
                        {option.productName}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {option.interval === "one_time"
                          ? "One-time payment"
                          : `${option.interval || "month"} billing`}
                      </p>
                    </div>
                    <div className="text-base font-bold text-gray-900">
                      {formatSubscriptionPrice(
                        option.amount,
                        option.currency?.toUpperCase?.() || "AUD",
                        option.interval ||
                          (option.priceType === "one_time"
                            ? "one_time"
                            : "month"),
                      )}
                    </div>
                    {option.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {option.description}
                      </p>
                    )}
                    {option.isPopular &&
                      !option.productName?.toLowerCase?.().includes("pro") && (
                        <span className="inline-flex mt-1 px-2 py-1 text-[11px] font-semibold text-[#F30099] bg-pink-100 rounded-full">
                          Popular
                        </span>
                      )}
                  </button>
                ))}

                {isPriceLoading && (
                  <div className="flex items-center justify-center px-3 py-6 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                    Loading plans...
                  </div>
                )}

                {!isPriceLoading && upgradeOptions.length === 0 && (
                  <div className="flex items-center justify-center px-3 py-6 border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                    No upgrade plans available right now.
                  </div>
                )}
              </div>
            </div>

            <div className="pt-1">
              <p className="text-sm text-gray-700">
                We’ll use your account contact details to manage this purchase
                with Stripe:
                <span className="font-medium">
                  {" "}
                  {checkoutEmail || userEmail}
                </span>{" "}
                and
                <span className="font-medium">
                  {" "}
                  {checkoutPhone || userPhone}
                </span>
                . If you need to change them, update your account first.
              </p>
            </div>

            {checkoutPriceSummary && (
              <div className="text-sm text-gray-700">
                <span className="font-medium">
                  {checkoutPriceSummary.productName || "Plan"}:
                </span>{" "}
                {formatSubscriptionPrice(
                  checkoutPriceSummary.amount,
                  checkoutPriceSummary.currency?.toUpperCase?.() || "AUD",
                  checkoutPriceSummary.interval ||
                    (checkoutPriceSummary.priceType === "one_time"
                      ? "one_time"
                      : "month"),
                ) || "Premium monthly"}
                {checkoutPriceSummary.priceType === "one_time" &&
                  " (one-off payment)"}
              </div>
            )}

            {checkoutError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 p-2 rounded">
                {checkoutError}
              </div>
            )}

            {!checkoutClientSecret && (
              <button
                type="button"
                onClick={() =>
                  startInAppCheckout(
                    checkoutEmail || userEmail,
                    checkoutPhone || toLocalAustralianMobile(userPhone),
                    selectedPriceId || defaultUpgradeOption?.priceId || null,
                  )
                }
                disabled={isCheckoutInitializing || !selectedPriceId}
                className="w-full px-4 py-2 bg-[#F30099] text-white rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-60"
              >
                {isCheckoutInitializing
                  ? "Preparing checkout..."
                  : "Continue to Payment"}
              </button>
            )}

            {checkoutClientSecret && stripePromise && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: checkoutClientSecret,
                  appearance: { theme: "stripe" },
                }}
              >
                <InAppCheckoutForm
                  email={checkoutEmail || userEmail}
                  phoneNumber={checkoutPhone || userPhone}
                  phoneNumberNormalized={normalizePhoneForStripe(
                    checkoutPhone || userPhone,
                  )}
                  onSuccess={handleCheckoutSuccess}
                  onError={handleCheckoutError}
                  onClose={closeInAppCheckout}
                  isDisabled={isCheckoutInitializing}
                />
              </Elements>
            )}

            {/* Plan changes re-initialize in the background; no manual refresh needed */}
          </div>
        </div>
      )}

      {/* Update Contact Modal */}
      <UpdateContactModal
        isOpen={updateModal.isOpen}
        type={updateModal.type}
        onClose={() => setUpdateModal({ isOpen: false, type: null })}
        onUpdate={handleUpdateContact}
        currentValue={updateModal.type === "email" ? userEmail : userPhone}
      />

      {/* Upgrade Loading Modal */}
      {isUpgradeLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto text-[#F30099] animate-spin mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Upgrading Your Plan
              </h2>
              <p className="text-gray-600 mb-4">
                Please wait while we update your subscription...
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-yellow-800 font-medium flex items-center justify-center gap-2">
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Do not refresh or close this page
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Session Expiration Component - only in development */}
      {/* {isDev && <TestSessionExpiration />} */}
    </div>
  );
}
